import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';

import {
  calendarSelectors,
  createEvent,
  deleteEvent,
  restoreEvent,
  fetchEvents,
  fetchRecords,
  updateEvent,
  transferEvent,
  deleteRecord,
  createRecord,
} from '@crm/features/calendar/calendarSlice';
import {
  directionsSelectors,
  directionsThunks,
} from '@crm/features/directions/directionsSlice';
import { managersSelectors, managersThunks } from '@crm/features/managers/managersSlice';
import { useBranchRealtime } from '@crm/hooks/useBranchRealtime';
import { crmApi, errMsg } from '@crm/api';
import { UiButton, UiInput, UiNativeSelect, UiSelect, UiTextarea, UiTimePicker } from '@shared/common/ui';
import { toDateOnlyString } from '@shared/helpers/dates';

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const MONTHS = ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'];

const COACH_PAYMENT_TYPE_OPTIONS = [
  { value: '1', label: 'Фиксированная' },
  { value: '2', label: 'Почасовая' },
  { value: '3', label: 'За занятие' },
  { value: '4', label: 'Процентная' },
];

const COACH_PAYMENT_TYPE_LABELS = {
  1: 'Фиксированная',
  2: 'Почасовая',
  3: 'За занятие',
  4: 'Процентная',
};

const CALENDAR_EVENT_STATUS = {
  ACTIVE: 1,
  CANCELLED: 2,
  MOVED: 3,
};

const CALENDAR_EVENT_STATUS_LABELS = {
  [CALENDAR_EVENT_STATUS.CANCELLED]: 'Отменено',
  [CALENDAR_EVENT_STATUS.MOVED]: 'Перенесено',
};

function isValidCssColor(value) {
  if (!value) return false;
  if (typeof window !== 'undefined' && window.CSS?.supports) {
    return window.CSS.supports('color', value);
  }
  return /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value) || /^[a-z]+$/i.test(value);
}

function getHallAccent(hall) {
  const color = String(hall?.color ?? '').trim();
  if (isValidCssColor(color)) return color;

  const fallbackSource = `${hall?.name ?? ''} ${color}`.toLowerCase();
  if (/черн|чёрн|black/.test(fallbackSource)) return '#111111';
  if (/красн|red/.test(fallbackSource)) return '#ef3124';
  if (/бел|white/.test(fallbackSource)) return '#4aa8f5';
  return null;
}

function getHallAccentStyle(hall, propertyName) {
  const accent = getHallAccent(hall);
  return accent ? { [propertyName]: accent } : undefined;
}

function formatCurrency(value) {
  if (value == null || value === '') return '—';
  return `${Number(value).toLocaleString('ru-RU')} ₽`;
}

function coachPaymentTypeLabel(type) {
  if (type == null || type === '') return '—';
  return COACH_PAYMENT_TYPE_LABELS[Number(type)] ?? `Тип ${type}`;
}

function coachPaymentAmount(amount, type) {
  if (amount == null || amount === '') return '—';
  return Number(type) === 4 ? `${amount}%` : formatCurrency(amount);
}

function tariffPriceLabel(price) {
  return formatCurrency(price);
}

function hallLabel(hallId, halls) {
  if (!hallId) return 'Без зала';
  return halls.find((hall) => hall.id === hallId)?.name ?? hallId;
}

function directionLabel(directionId, directions, event) {
  return event?.directionName || directions.find((direction) => direction.id === directionId)?.name || '—';
}

function isScheduleCheckItemReady(item) {
  if (!item?.hallId || !item.eventDate || !item.timeStart || !item.timeEnd) return false;
  return item.timeStart < item.timeEnd;
}

function eventsOverlap(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

function formatConflictMessages(conflicts) {
  return (conflicts ?? []).slice(0, 5).map((conflict) => {
    const name = conflict.conflictName || 'Без названия';
    const from = conflict.conflictDateFrom || conflict.eventDate || '';
    const to = conflict.conflictDateTo && conflict.conflictDateTo !== from ? `–${conflict.conflictDateTo}` : '';
    const timeStart = conflict.conflictTimeStart || conflict.timeStart || '';
    const timeEnd = conflict.conflictTimeEnd || conflict.timeEnd || '';
    return [`${name}`, `${from}${to}`, `${timeStart}–${timeEnd}`].filter(Boolean).join(' · ');
  });
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function toISODate(d) {
  return toDateOnlyString(d);
}

function formatTimeOnly(d) {
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

function currentMinute() {
  const now = new Date();
  now.setSeconds(0, 0);
  return now;
}

function useCurrentMinute() {
  const [nowMinute, setNowMinute] = useState(currentMinute);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMinute(currentMinute()), 15000);
    return () => window.clearInterval(timer);
  }, []);

  return nowMinute;
}

function isCalendarEventLockedByTime(event, nowMinute) {
  const day = event?.day?.slice(0, 10);
  const time = event?.timeStart?.slice(0, 5);
  if (!day || !time) return false;
  return `${day} ${time}` <= `${toDateOnlyString(nowMinute)} ${formatTimeOnly(nowMinute)}`;
}

function calendarEventStatus(event) {
  return Number(event?.status ?? CALENDAR_EVENT_STATUS.ACTIVE);
}

function isCalendarEventActive(event) {
  return calendarEventStatus(event) === CALENDAR_EVENT_STATUS.ACTIVE;
}

function calendarEventStatusLabel(event) {
  return CALENDAR_EVENT_STATUS_LABELS[calendarEventStatus(event)] ?? '';
}

function formatEventDateTime(event) {
  const day = event?.day?.slice(0, 10);
  if (!day) return '';
  const [year, month, date] = day.split('-');
  const timeStart = event?.timeStart?.slice(0, 5);
  const timeEnd = event?.timeEnd?.slice(0, 5);
  const dateText = `${date}.${month}.${year}`;
  if (timeStart && timeEnd) return `${dateText} ${timeStart}–${timeEnd}`;
  return dateText;
}

function startOfWeek(date) {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function CalendarPage() {
  const { branchId } = useParams();
  const dispatch = useDispatch();

  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [editing, setEditing] = useState(null);
  const [halls, setHalls] = useState([]);
  const [hallFilter, setHallFilter] = useState('');
  const nowMinute = useCurrentMinute();

  const from = toISODate(weekStart);
  const to = toISODate(addDays(weekStart, 6));

  const events = useSelector(calendarSelectors.selectEvents(branchId));
  const status = useSelector(calendarSelectors.selectStatus(branchId));

  useEffect(() => {
    if (branchId) dispatch(fetchEvents({ branchId, from, to }));
  }, [dispatch, branchId, from, to]);

  useEffect(() => {
    if (!branchId) return undefined;
    let alive = true;
    crmApi(branchId)
      .get('/halls')
      .then(({ data }) => {
        if (alive) setHalls(data.halls ?? []);
      })
      .catch(() => {
        if (alive) setHalls([]);
      });
    return () => {
      alive = false;
    };
  }, [branchId]);

  useEffect(() => {
    if (!hallFilter) return;
    if (!halls.some((hall) => hall.id === hallFilter)) {
      setHallFilter('');
    }
  }, [hallFilter, halls]);

  useBranchRealtime(branchId, (action) => {
    if (action.startsWith('calendar:event.')) {
      dispatch(fetchEvents({ branchId, from, to }));
    }
  });

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const filteredEvents = useMemo(
    () => (hallFilter ? events.filter((ev) => ev.hallId === hallFilter) : events),
    [events, hallFilter],
  );

  const byDay = useMemo(() => {
    const map = {};
    for (const ev of filteredEvents) {
      const day = ev.day?.slice(0, 10);
      if (!day) continue;
      (map[day] ||= []).push(ev);
    }
    for (const k of Object.keys(map)) {
      map[k].sort((a, b) => (a.timeStart < b.timeStart ? -1 : 1));
    }
    return map;
  }, [filteredEvents]);

  const hallsById = useMemo(() => new Map(halls.map((hall) => [hall.id, hall])), [halls]);
  const showHallFilter = halls.length > 0;
  const showEventHalls = halls.length > 1;

  return (
    <section>
      <header className="page-header">
        <h1>Календарь</h1>
        <UiButton variant="ghost" size="sm"
          
          type="button"
          onClick={() => setWeekStart(addDays(weekStart, -7))}
        >
          ← Неделя
        </UiButton>
        <UiButton variant="ghost" size="sm"
          
          type="button"
          onClick={() => setWeekStart(startOfWeek(new Date()))}
        >
          Сегодня
        </UiButton>
        <UiButton variant="ghost" size="sm"
          
          type="button"
          onClick={() => setWeekStart(addDays(weekStart, 7))}
        >
          Неделя →
        </UiButton>
        <span style={{ color: 'var(--color-muted)', marginLeft: 12 }}>
          {from} — {to}
        </span>
        <div className="page-header__spacer" />
        <UiButton
          
          type="button"
          onClick={() => setEditing({ day: toISODate(new Date()) })}
        >
          Новое событие
        </UiButton>
      </header>

      {showHallFilter && (
        <div className="calendar-hall-filter" aria-label="Фильтр по залам">
          <button
            type="button"
            className={`calendar-hall-filter__button${!hallFilter ? ' is-active' : ''}`}
            onClick={() => setHallFilter('')}
          >
            Все залы
          </button>
          {halls.map((hall) => (
            <button
              key={hall.id}
              type="button"
              className={`calendar-hall-filter__button${hallFilter === hall.id ? ' is-active' : ''}`}
              style={getHallAccentStyle(hall, '--calendar-hall-filter-accent')}
              onClick={() => setHallFilter(hall.id)}
            >
              <span className="calendar-hall-filter__dot" />
              {hall.name ?? hall.id}
            </button>
          ))}
        </div>
      )}

      <div className="calendar-week">
        {days.map((d) => {
          const day = toISODate(d);
          const list = byDay[day] ?? [];
          return (
            <div key={day} className="calendar-day">
              <header className="calendar-day__header">
                <span className="calendar-day__weekday">{WEEKDAYS[(d.getDay() + 6) % 7]}</span>
                <span className="calendar-day__month">{MONTHS[d.getMonth()]}</span>
                <span className="calendar-day__date">{d.getDate()}</span>
              </header>
              <div className="calendar-day__body">
                {list.map((ev) => (
                  <CalendarEventCard
                    key={ev.id}
                    event={ev}
                    hall={showEventHalls && ev.hallId ? hallsById.get(ev.hallId) : null}
                    locked={!isCalendarEventActive(ev) || isCalendarEventLockedByTime(ev, nowMinute)}
                    onClick={() => setEditing(ev)}
                  />
                ))}
                <UiButton variant="ghost" size="sm"
                  type="button"
                   className="calendar-day__add"
                  onClick={() => setEditing({ day })}
                >
                  + Событие
                </UiButton>
              </div>
            </div>
          );
        })}
      </div>

      {status === 'loading' && <div className="placeholder">Загрузка…</div>}

      {editing && (
        <EventDrawer
          branchId={branchId}
          event={editing}
          halls={halls}
          onClose={() => setEditing(null)}
          onDeleted={() => {
            setEditing(null);
          }}
        />
      )}
    </section>
  );
}

function CalendarEventCard({ event, hall, locked, onClick }) {
  const eventStyle = getHallAccentStyle(hall, '--calendar-event-accent');
  const status = calendarEventStatus(event);
  const statusLabel = calendarEventStatusLabel(event);
  const statusClass = status === CALENDAR_EVENT_STATUS.CANCELLED
    ? ' is-cancelled'
    : status === CALENDAR_EVENT_STATUS.MOVED
      ? ' is-moved'
      : '';

  return (
    <button
      type="button"
      className={`calendar-event${locked ? ' is-locked' : ''}${statusClass}`}
      style={eventStyle}
      onClick={onClick}
      title={statusLabel || (locked ? 'Время события уже наступило' : undefined)}
    >
      <span className="calendar-event__time">
        {event.timeStart?.slice(0, 5)}–{event.timeEnd?.slice(0, 5)}
      </span>
      {statusLabel ? <span className="calendar-event__status">{statusLabel}</span> : null}
      <span className="calendar-event__title">
        {event.name ?? event.directionName ?? '(без названия)'}
      </span>
      {hall && (
        <span className="calendar-event__hall">
          <span className="calendar-event__hall-dot" />
          {hall.name ?? event.hallId}
        </span>
      )}
    </button>
  );
}

function EventDrawer({ branchId, event, halls, onClose }) {
  const dispatch = useDispatch();
  const isNew = !event.id;
  const nowMinute = useCurrentMinute();
  const statusLabel = calendarEventStatusLabel(event);
  const isInactive = !isNew && !isCalendarEventActive(event);
  const isTimeLocked = !isNew && isCalendarEventLockedByTime(event, nowMinute);
  const isLocked = isInactive || isTimeLocked;
  const isCancelled = !isNew && calendarEventStatus(event) === CALENDAR_EVENT_STATUS.CANCELLED;
  const isMoved = !isNew && calendarEventStatus(event) === CALENDAR_EVENT_STATUS.MOVED;
  const canRestore = isCancelled && !isTimeLocked;
  const inactiveStatusText = statusLabel || 'Неактивно';
  const lockMessage = isInactive
    ? `Событие ${inactiveStatusText.toLowerCase()}. Редактирование, перенос и отмена недоступны.`
    : 'Время события уже наступило. Редактирование, перенос и отмена недоступны.';
  const directions = useSelector(directionsSelectors.selectList(branchId));
  const managers = useSelector(managersSelectors.selectList(branchId));
  const branchEvents = useSelector(calendarSelectors.selectEvents(branchId));
  const showHallField = halls.length > 1;
  const [form, setForm] = useState(() => ({
    day: event.day ? event.day.slice(0, 10) : '',
    timeStart: event.timeStart ?? '10:00',
    timeEnd: event.timeEnd ?? '11:00',
    directionId: event.directionId ?? '',
    coachId: event.coachId ?? '',
    hallId: event.hallId ?? '',
    name: event.name ?? '',
    addInfo: event.addInfo ?? '',
    coachSalaryType: event.coachSalaryType == null ? '' : String(event.coachSalaryType),
    coachSalaryAmount: event.coachSalaryAmount ?? '',
  }));
  const [directionDetail, setDirectionDetail] = useState(null);
  const [directionError, setDirectionError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [visitCount, setVisitCount] = useState(event.visitCount ?? 0);
  const [transferOpen, setTransferOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [movedToEvent, setMovedToEvent] = useState(null);
  const [movedFromEvent, setMovedFromEvent] = useState(null);

  const movedToId = event.movedToId ?? null;
  useEffect(() => {
    if (!isMoved || !movedToId) {
      setMovedToEvent(null);
      return undefined;
    }
    const fromList = branchEvents.find((e) => e.id === movedToId);
    if (fromList) {
      setMovedToEvent(fromList);
      return undefined;
    }
    let alive = true;
    crmApi(branchId)
      .get(`/calendar/${movedToId}`)
      .then(({ data }) => {
        if (alive) setMovedToEvent(data.event ?? null);
      })
      .catch(() => {
        if (alive) setMovedToEvent(null);
      });
    return () => {
      alive = false;
    };
  }, [branchId, isMoved, movedToId, branchEvents]);

  const movedFromId = event.movedFromId ?? null;
  useEffect(() => {
    if (isNew || !movedFromId) {
      setMovedFromEvent(null);
      return undefined;
    }
    const fromList = branchEvents.find((e) => e.id === movedFromId);
    if (fromList) {
      setMovedFromEvent(fromList);
      return undefined;
    }
    let alive = true;
    crmApi(branchId)
      .get(`/calendar/${movedFromId}`)
      .then(({ data }) => {
        if (alive) setMovedFromEvent(data.event ?? null);
      })
      .catch(() => {
        if (alive) setMovedFromEvent(null);
      });
    return () => {
      alive = false;
    };
  }, [branchId, isNew, movedFromId, branchEvents]);

  const movedToText = isMoved && movedToEvent ? formatEventDateTime(movedToEvent) : '';
  const movedFromText = movedFromEvent ? formatEventDateTime(movedFromEvent) : '';

  useEffect(() => {
    if (!directions.length) dispatch(directionsThunks.list({ branchId }));
    if (!managers.length) dispatch(managersThunks.list({ branchId }));
  }, [dispatch, branchId, directions.length, managers.length]);

  useEffect(() => {
    setForm({
      day: event.day ? event.day.slice(0, 10) : '',
      timeStart: event.timeStart ?? '10:00',
      timeEnd: event.timeEnd ?? '11:00',
      directionId: event.directionId ?? '',
      coachId: event.coachId ?? '',
      hallId: event.hallId ?? '',
      name: event.name ?? '',
      addInfo: event.addInfo ?? '',
      coachSalaryType: event.coachSalaryType == null ? '' : String(event.coachSalaryType),
      coachSalaryAmount: event.coachSalaryAmount ?? '',
    });
    setVisitCount(event.visitCount ?? 0);
  }, [event]);

  useEffect(() => {
    const directionId = form.directionId || event.directionId;
    if (!branchId || !directionId) {
      setDirectionDetail(null);
      setDirectionError(null);
      return undefined;
    }
    let alive = true;
    setDirectionError(null);
    crmApi(branchId)
      .get(`/directions/${directionId}`)
      .then(({ data }) => {
        if (alive) setDirectionDetail(data.direction ?? null);
      })
      .catch((e) => {
        if (!alive) return;
        setDirectionDetail(null);
        setDirectionError(errMsg(e, 'Не удалось загрузить тарифы направления'));
      });
    return () => {
      alive = false;
    };
  }, [branchId, event.directionId, form.directionId]);

  const managerOptions = useMemo(
    () => [
      { value: '', label: '—' },
      ...managers.map((manager) => ({
        value: manager.id,
        label: manager.name ?? manager.login ?? manager.id,
      })),
    ],
    [managers],
  );

  const selectedCoachDefaults = useMemo(
    () => (directionDetail?.coaches ?? []).find((coach) => coach.managerId === form.coachId) ?? null,
    [directionDetail?.coaches, form.coachId],
  );

  const setField = (field, value) => {
    if (isLocked) return;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const onCoachChange = (coachId) => {
    if (isLocked) return;
    const coach = (directionDetail?.coaches ?? []).find((item) => item.managerId === coachId);
    setForm((prev) => ({
      ...prev,
      coachId,
      coachSalaryType: coach?.typeCalculation == null ? prev.coachSalaryType : String(coach.typeCalculation),
      coachSalaryAmount: coach?.amount == null ? prev.coachSalaryAmount : coach.amount,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (isLocked) {
      setSaveError('Время события уже наступило. Редактирование недоступно.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    const payload = isNew
      ? {
          day: form.day,
          timeStart: form.timeStart,
          timeEnd: form.timeEnd,
          directionId: form.directionId || null,
          coachId: form.coachId || null,
          hallId: showHallField || form.hallId ? form.hallId || null : undefined,
          name: form.name.trim() || null,
          addInfo: form.addInfo.trim() || null,
          coachSalaryType: form.coachSalaryType === '' ? null : Number(form.coachSalaryType),
          coachSalaryAmount: form.coachSalaryAmount === '' ? null : Number(form.coachSalaryAmount),
        }
      : {
          coachId: form.coachId || null,
          name: form.name.trim() || null,
          addInfo: form.addInfo.trim() || null,
          coachSalaryType: form.coachSalaryType === '' ? null : Number(form.coachSalaryType),
          coachSalaryAmount: form.coachSalaryAmount === '' ? null : Number(form.coachSalaryAmount),
        };
    const action = isNew
      ? createEvent({ branchId, payload })
      : updateEvent({ branchId, id: event.id, payload });
    const res = await dispatch(action);
    setSaving(false);
    if (res.meta.requestStatus === 'fulfilled') onClose();
    else setSaveError(res.payload || 'Не удалось сохранить событие');
  };

  const onRestore = async () => {
    if (!canRestore || restoring) return;
    setSaveError(null);
    setRestoring(true);
    const res = await dispatch(restoreEvent({ branchId, id: event.id }));
    setRestoring(false);
    if (res.meta.requestStatus === 'fulfilled') onClose();
    else setSaveError(res.payload || 'Не удалось вернуть событие');
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <header className="drawer__header">
          <h2>{isNew ? 'Новое событие' : 'Событие'}</h2>
          <UiButton variant="ghost" type="button" onClick={onClose}>
            Закрыть
          </UiButton>
        </header>

        <form onSubmit={onSubmit}>
          {movedFromText ? (
            <div className="calendar-event-drawer__notice">
              Перенос с{' '}
              <span className="calendar-event-drawer__moved-from">{movedFromText}</span>
            </div>
          ) : null}
          {isLocked ? (
            <div className="calendar-event-drawer__notice">
              {lockMessage}
              {movedToText ? (
                <>
                  {' '}
                  Перенесено на{' '}
                  <span className="calendar-event-drawer__moved-to">{movedToText}</span>
                </>
              ) : null}
            </div>
          ) : null}
          <div className="form-grid">
            {isNew ? (
              <>
                <UiInput className="field" label="Дата" type="date" value={form.day} onChange={(e) => setField('day', e.target.value)} required />
                <UiTimePicker className="field" label="Начало" value={form.timeStart} onChange={(e) => setField('timeStart', e.target.value)} required />
                <UiTimePicker className="field" label="Конец" value={form.timeEnd} onChange={(e) => setField('timeEnd', e.target.value)} required />
                <div className="field">
                  <label>Направление</label>
                  <UiNativeSelect value={form.directionId} onChange={(e) => setField('directionId', e.target.value)}>
                    <option value="">—</option>
                    {directions.map((direction) => (
                      <option key={direction.id} value={direction.id}>
                        {direction.name}
                      </option>
                    ))}
                  </UiNativeSelect>
                </div>
                {showHallField && (
                  <div className="field">
                    <label>Зал</label>
                    <UiNativeSelect value={form.hallId} onChange={(e) => setField('hallId', e.target.value)}>
                      <option value="">—</option>
                      {halls.map((hall) => (
                        <option key={hall.id} value={hall.id}>
                          {hall.name ?? hall.id}
                        </option>
                      ))}
                    </UiNativeSelect>
                  </div>
                )}
              </>
            ) : (
              <>
                <InfoField label="Дата" value={form.day || '—'} />
                <InfoField label="Начало" value={form.timeStart?.slice(0, 5) || '—'} />
                <InfoField label="Конец" value={form.timeEnd?.slice(0, 5) || '—'} />
                <InfoField label="Направление" value={directionLabel(form.directionId, directions, event)} />
                <InfoField label="Зал" value={hallLabel(form.hallId, halls)} />
                <InfoField label="Посещений" value={String(visitCount)} />
                {isInactive ? <InfoField label="Статус" value={inactiveStatusText} /> : null}
              </>
            )}

            <UiSelect className="field" label="Тренер" value={form.coachId} onChange={onCoachChange} options={managerOptions} placeholder="Выберите тренера" disabled={isLocked} />
            <UiSelect className="field" label="Тип ставки" value={form.coachSalaryType} onChange={(value) => setField('coachSalaryType', value)} options={[{ value: '', label: '—' }, ...COACH_PAYMENT_TYPE_OPTIONS]} disabled={isLocked} />
            <UiInput className="field" label="Сумма ставки" type="number" value={form.coachSalaryAmount} onChange={(e) => setField('coachSalaryAmount', e.target.value)} disabled={isLocked} />
            {selectedCoachDefaults && (
              <InfoField
                label="Ставка направления"
                value={`${coachPaymentTypeLabel(selectedCoachDefaults.typeCalculation)} · ${coachPaymentAmount(selectedCoachDefaults.amount, selectedCoachDefaults.typeCalculation)}`}
              />
            )}
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Название (опционально)</label>
              <UiInput value={form.name} onChange={(e) => setField('name', e.target.value)} disabled={isLocked} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>Комментарий</label>
              <UiTextarea rows={2} value={form.addInfo} onChange={(e) => setField('addInfo', e.target.value)} disabled={isLocked} />
            </div>
          </div>

          <section className="calendar-event-drawer__tariffs">
            <h3>Тарифы направления</h3>
            {directionError ? <div className="form-error">{directionError}</div> : null}
            {(directionDetail?.tariffs ?? []).length ? (
              <ul className="plain-list">
                {directionDetail.tariffs.map((tariff) => (
                  <li key={tariff.tariffId} className="plain-list__item">
                    <span>{tariff.tariffName ?? tariff.tariffId}</span>
                    <strong>{tariffPriceLabel(tariff.price)}</strong>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="calendar-event-drawer__empty">Тарифы не настроены</div>
            )}
          </section>

          {saveError ? <div className="form-error">{saveError}</div> : null}

          <div className="form-actions">
            {!isLocked && (
              <UiButton type="submit" disabled={saving}>
                {saving ? 'Сохранение…' : 'Сохранить'}
              </UiButton>
            )}
            <UiButton variant="ghost" type="button" onClick={onClose}>
              {isLocked ? 'Закрыть' : 'Отменить изменения'}
            </UiButton>
            {isCancelled && (
              <UiButton
                type="button"
                onClick={onRestore}
                disabled={!canRestore || restoring}
                title={canRestore ? undefined : 'Время события уже наступило. Возврат недоступен.'}
              >
                {restoring ? 'Возврат…' : 'Вернуть в активные'}
              </UiButton>
            )}
            {!isNew && (
              <UiButton variant="ghost" type="button" onClick={() => setTransferOpen(true)} disabled={isLocked} title={isLocked ? lockMessage : undefined}>
                Перенести
              </UiButton>
            )}
            {!isNew && (
              <UiButton
                variant="danger"
                type="button"
                disabled={isLocked}
                title={isLocked ? lockMessage : undefined}
                onClick={async () => {
                  if (isLocked) return;
                  if (!window.confirm('Отменить событие?')) return;
                  await dispatch(deleteEvent({ branchId, id: event.id }));
                  onClose();
                }}
              >
                Отменить
              </UiButton>
            )}
          </div>
        </form>

        {!isNew && (
          <Records
            branchId={branchId}
            day={event.day?.slice(0, 10)}
            event={event}
            onVisitMarked={() => setVisitCount((prev) => prev + 1)}
          />
        )}

        {!isLocked && transferOpen && (
          <TransferEventModal
            branchId={branchId}
            event={event}
            halls={halls}
            initialDraft={form}
            onClose={() => setTransferOpen(false)}
            onTransferred={onClose}
          />
        )}
      </div>
    </div>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="calendar-event-info">
      <span className="calendar-event-info__label">{label}</span>
      <span className="calendar-event-info__value">{value}</span>
    </div>
  );
}

function TransferEventModal({ branchId, event, halls, initialDraft, onClose, onTransferred }) {
  const dispatch = useDispatch();
  const calendarEvents = useSelector(calendarSelectors.selectEvents(branchId));
  const nowMinute = useCurrentMinute();
  const [draft, setDraft] = useState({
    hallId: initialDraft.hallId || '',
    eventDate: initialDraft.day || '',
    timeStart: initialDraft.timeStart || '10:00',
    timeEnd: initialDraft.timeEnd || '11:00',
  });
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [remoteConflictMessages, setRemoteConflictMessages] = useState([]);
  const [remoteChecking, setRemoteChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const hallOptions = useMemo(
    () => halls.map((hall) => ({ value: hall.id, label: hall.name ?? hall.id })),
    [halls],
  );
  const slotUnchanged = (
    draft.hallId === (initialDraft.hallId || '')
    && draft.eventDate === (initialDraft.day || '')
    && draft.timeStart === (initialDraft.timeStart || '10:00')
    && draft.timeEnd === (initialDraft.timeEnd || '11:00')
  );
  const targetLocked = isCalendarEventLockedByTime({ day: draft.eventDate, timeStart: draft.timeStart }, nowMinute);
  const localConflictMessages = useMemo(() => {
    if (!draft.hallId || !draft.eventDate || !draft.timeStart || !draft.timeEnd) return [];
    return calendarEvents
      .filter((calendarEvent) => calendarEvent.id !== event.id)
      .filter(isCalendarEventActive)
      .filter((calendarEvent) => calendarEvent.day?.slice(0, 10) === draft.eventDate)
      .filter((calendarEvent) => calendarEvent.hallId === draft.hallId)
      .filter((calendarEvent) => eventsOverlap(
        draft.timeStart,
        draft.timeEnd,
        calendarEvent.timeStart?.slice(0, 5),
        calendarEvent.timeEnd?.slice(0, 5),
      ))
      .slice(0, 5)
      .map((calendarEvent) => (
        [
          calendarEvent.name ?? calendarEvent.directionName ?? 'Без названия',
          calendarEvent.day?.slice(0, 10),
          `${calendarEvent.timeStart?.slice(0, 5)}–${calendarEvent.timeEnd?.slice(0, 5)}`,
          hallLabel(calendarEvent.hallId, halls),
        ].filter(Boolean).join(' · ')
      ));
  }, [calendarEvents, draft, event.id, halls]);
  const visibleMessages = slotUnchanged
    ? ['Выбраны текущие дата, время и зал этого занятия']
    : localConflictMessages.length
      ? localConflictMessages
      : remoteConflictMessages.length
        ? remoteConflictMessages
        : messages;
  const visibleError = slotUnchanged
    ? 'Выберите новый слот для переноса'
    : targetLocked
      ? 'Нельзя перенести событие в прошедшее время'
    : localConflictMessages.length || remoteConflictMessages.length
      ? 'Зал занят в выбранное время'
      : error;
  const hasConflict = visibleMessages.length > 0 || targetLocked;
  const setDraftField = (field, value) => {
    setError(null);
    setMessages([]);
    setRemoteConflictMessages([]);
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    const item = {
      hallId: draft.hallId || null,
      eventDate: draft.eventDate,
      timeStart: draft.timeStart,
      timeEnd: draft.timeEnd,
    };
    if (slotUnchanged || targetLocked || localConflictMessages.length || !isScheduleCheckItemReady(item)) {
      setRemoteConflictMessages([]);
      setRemoteChecking(false);
      return undefined;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setRemoteChecking(true);
      try {
        const { data } = await crmApi(branchId).get('/calendar', {
          params: { from: draft.eventDate, to: draft.eventDate },
        });
        if (!alive) return;
        const conflicts = (data.events ?? [])
          .filter((calendarEvent) => calendarEvent.id !== event.id)
          .filter(isCalendarEventActive)
          .filter((calendarEvent) => calendarEvent.hallId === draft.hallId)
          .filter((calendarEvent) => eventsOverlap(
            draft.timeStart,
            draft.timeEnd,
            calendarEvent.timeStart?.slice(0, 5),
            calendarEvent.timeEnd?.slice(0, 5),
          ))
          .slice(0, 5)
          .map((calendarEvent) => (
            [
              calendarEvent.name ?? calendarEvent.directionName ?? 'Без названия',
              calendarEvent.day?.slice(0, 10),
              `${calendarEvent.timeStart?.slice(0, 5)}–${calendarEvent.timeEnd?.slice(0, 5)}`,
              hallLabel(calendarEvent.hallId, halls),
            ].filter(Boolean).join(' · ')
          ));
        setRemoteConflictMessages(conflicts);
      } catch (e) {
        if (alive) setError(errMsg(e, 'Не удалось проверить занятость зала'));
      } finally {
        if (alive) setRemoteChecking(false);
      }
    }, 250);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [branchId, draft, event.id, halls, localConflictMessages.length, slotUnchanged, targetLocked]);

  const checkAvailable = async () => {
    const item = {
      key: 'transfer',
      hallId: draft.hallId || null,
      eventDate: draft.eventDate,
      timeStart: draft.timeStart,
      timeEnd: draft.timeEnd,
    };
    if (!event.directionId) {
      setError('Для проверки занятости нужно направление события');
      return false;
    }
    if (!isScheduleCheckItemReady(item)) {
      setError('Выберите зал, дату и корректное время');
      return false;
    }
    if (targetLocked) {
      setError('Нельзя перенести событие в прошедшее время');
      return false;
    }
    if (localConflictMessages.length) {
      setError('Зал занят в выбранное время');
      return false;
    }
    if (slotUnchanged) {
      setError('Выберите новый слот для переноса');
      return false;
    }
    if (remoteConflictMessages.length) {
      setError('Зал занят в выбранное время');
      return false;
    }
    const { data } = await crmApi(branchId).post(`/directions/${event.directionId}/schedule/conflicts`, {
      mode: 'event',
      ignoreCalendarId: event.id,
      items: [item],
    });
    const conflicts = data.conflicts ?? [];
    setMessages(formatConflictMessages(conflicts));
    if (conflicts.length) {
      setError('Зал занят в выбранное время');
      return false;
    }
    return true;
  };

  const onTransfer = async () => {
    setError(null);
    setMessages([]);
    setSaving(true);
    try {
      const available = await checkAvailable();
      if (!available) return;
      const res = await dispatch(transferEvent({
        branchId,
        id: event.id,
        payload: {
          day: draft.eventDate,
          timeStart: draft.timeStart,
          timeEnd: draft.timeEnd,
          hallId: draft.hallId || null,
        },
      }));
      if (res.meta.requestStatus === 'fulfilled') onTransferred();
      else setError(res.payload || 'Не удалось перенести событие');
    } catch (e) {
      setError(errMsg(e, 'Не удалось проверить занятость зала'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-transfer-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-card__header">
          <h2 id="calendar-transfer-title">Перенос занятия</h2>
          <UiButton variant="ghost" size="sm" type="button" onClick={onClose}>
            ×
          </UiButton>
        </header>
        <div className="modal-card__body">
          <div className="form-grid">
            <UiSelect
              className="field"
              label="Зал"
              value={draft.hallId}
              onChange={(value) => setDraftField('hallId', value)}
              options={hallOptions}
              placeholder="Выберите зал"
              error={hasConflict ? 'Зал занят' : null}
            />
            <div
              style={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-start' }}
            >
              <UiInput
                label="Дата"
                type="date"
                min={toDateOnlyString(nowMinute)}
                value={draft.eventDate}
                onChange={(e) => setDraftField('eventDate', e.target.value)}
                inputClassName={hasConflict ? 'is-invalid' : ''}
                style={{ flex: 2 }}
              />
              <UiTimePicker
                label="Начало"
                value={draft.timeStart}
                onChange={(e) => setDraftField('timeStart', e.target.value)}
                inputClassName={hasConflict ? 'is-invalid' : ''}
                style={{ flex: 1 }}
              />
              <UiTimePicker
                label="Конец"
                value={draft.timeEnd}
                onChange={(e) => setDraftField('timeEnd', e.target.value)}
                inputClassName={hasConflict ? 'is-invalid' : ''}
                style={{ flex: 1 }}
              />
            </div>
          </div>
          {visibleError ? <div className="form-error">{visibleError}</div> : null}
          {visibleMessages.length ? (
            <ul className="direction-conflict-modal__list">
              {visibleMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          ) : null}
          <div className="modal-card__actions">
            <UiButton
              type="button"
              onClick={onTransfer}
              disabled={saving || remoteChecking || slotUnchanged || targetLocked || localConflictMessages.length > 0 || remoteConflictMessages.length > 0}
            >
              {saving || remoteChecking ? 'Проверка…' : 'Перенести'}
            </UiButton>
            <UiButton variant="ghost" type="button" onClick={onClose}>
              Отмена
            </UiButton>
          </div>
        </div>
      </div>
    </div>
  );
}

function Records({ branchId, day, event, onVisitMarked }) {
  const dispatch = useDispatch();
  const records = useSelector(calendarSelectors.selectRecords(branchId, day));
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (day) dispatch(fetchRecords({ branchId, day }));
  }, [dispatch, branchId, day]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { clientId: '', info: '' } });

  const onAdd = async (values) => {
    setErr(null);
    const payload = {
      day,
      directionId: event.directionId ?? null,
      clientId: values.clientId || null,
      info: values.info.trim() || null,
    };
    const res = await dispatch(createRecord({ branchId, payload }));
    if (res.meta.requestStatus === 'fulfilled') reset({ clientId: '', info: '' });
    else setErr(res.payload);
  };

  const markVisited = async (record) => {
    try {
      await crmApi(branchId).post('/calendar/visitings', {
        day,
        clientId: record.clientId,
        directionId: event.directionId ?? null,
        visitIn: new Date().toISOString(),
      });
      onVisitMarked?.();
    } catch (e) {
      setErr(errMsg(e, 'Не удалось отметить посещение'));
    }
  };

  return (
    <section style={{ marginTop: 24 }}>
      <h3>Записи</h3>
      <ul className="plain-list">
        {records
          .filter((r) => !event.directionId || r.directionId === event.directionId)
          .map((r) => (
            <li key={r.id} className="plain-list__item">
              <span>
                {r.clientFio ?? r.clientId ?? '—'}
                {r.info && (
                  <span style={{ color: 'var(--color-muted)', marginLeft: 8 }}>{r.info}</span>
                )}
              </span>
              <span style={{ display: 'flex', gap: 6 }}>
                {r.clientId && (
                  <UiButton variant="ghost" size="sm"
                    
                    type="button"
                    onClick={() => markVisited(r)}
                  >
                    Отметить визит
                  </UiButton>
                )}
                <UiButton variant="danger" size="sm"
                  
                  type="button"
                  onClick={() => dispatch(deleteRecord({ branchId, day, id: r.id }))}
                >
                  ✕
                </UiButton>
              </span>
            </li>
          ))}
        {!records.length && <li style={{ color: 'var(--color-muted)' }}>Записей нет</li>}
      </ul>

      <form
        onSubmit={handleSubmit(onAdd)}
        style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12 }}
      >
        <div className="field" style={{ flex: 1 }}>
          <label>UUID клиента (или оставьте пустым — разовая запись)</label>
          <UiInput {...register('clientId')} placeholder="необязательно" />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Комментарий</label>
          <UiInput {...register('info')} />
        </div>
        <UiButton type="submit" disabled={isSubmitting}>
          Записать
        </UiButton>
      </form>
      {err && <div className="form-error">{err}</div>}
    </section>
  );
}
